import * as React from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/shadcn/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/shadcn/popover"
import { Badge } from "@/components/ui/shadcn/badge"

interface MultiSelectOption {
  value: string
  label: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  className?: string
  disabled?: boolean
  maxSelected?: number
  displayMode?: "badges" | "count" | "auto"
  maxBadges?: number
  side?: "top" | "bottom" | "left" | "right"
  align?: "start" | "center" | "end"
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select options...",
  searchPlaceholder = "Search...",
  emptyText = "No options found.",
  className,
  disabled = false,
  maxSelected,
  displayMode = "badges",
  maxBadges = 3,
  side,
  align = "start",
}: MultiSelectProps) {
  const handleSelect = (value: string) => {
    const isSelected = selected.includes(value)
    
    if (isSelected) {
      // Remove from selection
      onChange(selected.filter(item => item !== value))
    } else {
      // Add to selection (check max limit)
      if (!maxSelected || selected.length < maxSelected) {
        onChange([...selected, value])
      }
    }
  }

  const handleRemove = (value: string) => {
    onChange(selected.filter(item => item !== value))
  }

  const selectedOptions = options.filter(option => selected.includes(option.value))
  const showCount = displayMode === "count" || (displayMode === "auto" && selectedOptions.length > maxBadges)
  const visibleOptions =
    displayMode === "auto" ? selectedOptions.slice(0, maxBadges) : selectedOptions

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          className={cn(
            "flex h-auto min-h-[40px] w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            disabled && "cursor-not-allowed opacity-50",
            className
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {selectedOptions.length > 0 ? (
              showCount ? (
                <span className="text-muted-foreground">
                  {selectedOptions.length} selected
                </span>
              ) : (
                <>
                  {visibleOptions.map((option) => (
                <Badge
                  key={option.value}
                  variant="secondary"
                  className="mr-1 mb-1"
                >
                  {option.label}
                  <button
                    className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleRemove(option.value)
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onClick={() => handleRemove(option.value)}
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </Badge>
                  ))}
                  {displayMode === "auto" && selectedOptions.length > maxBadges && (
                    <Badge variant="secondary" className="mr-1 mb-1">
                      +{selectedOptions.length - maxBadges}
                    </Badge>
                  )}
                </>
              )
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] overflow-hidden p-0"
        align={align}
        side={side}
        sideOffset={4}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          {/* Show ~5 entries by default; the rest scrolls. */}
          <CommandList className="max-h-[200px]">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.includes(option.value)
                const isDisabled = maxSelected ? selected.length >= maxSelected && !isSelected : false
                
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    keywords={[option.label, option.value]}
                    onSelect={() => !isDisabled && handleSelect(option.value)}
                    className={cn(
                      isDisabled && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.label}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
} 
